import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import Message from "@/components/Message";
import InputBox from "@/components/InputBox";
import PageBreathingAura from "@/components/PageBreathingAura";

const AI_RESPONSES = [
  "That's an interesting thought. Tell me more about it.",
  "I see what you mean. Let me think about that.",
  "Great question! Here's what I think...",
  "I appreciate you sharing that with me.",
  "That resonates with me. Here's a different perspective.",
  "Fascinating. I'd love to explore that further.",
  "You're making a good point there.",
  "Let me reflect on that for a moment.",
  "I think that's a really valuable insight.",
  "That's a perspective I hadn't considered before.",
];

const USER_MESSAGES = [
  "I want a better morning routine.",
  "My focus drops in the afternoon.",
  "I missed my meditation session yesterday.",
  "What should I do when motivation is low?",
  "I am trying to improve my sleep schedule.",
  "Can you help me stay consistent this week?",
  "I felt calmer after breathwork today.",
  "What is a realistic habit to start with?",
  "I keep getting distracted by my phone.",
  "How do I reset after a bad day?",
  "I want to build a stronger reading habit.",
  "What is the easiest way to start journaling?",
  "I am doing better with hydration lately.",
  "Can I combine walking with mindfulness?",
  "I want a simple evening wind-down routine.",
  "How do I keep momentum after small wins?",
  "I am trying to reduce stress before work.",
  "What should I track every day?",
  "I finally completed my workout streak this week.",
  "How do I make my habits stick long term?",
];

const AI_REPLIES = [
  "Start small and make the first step easy to repeat.",
  "A short reset routine can help you regain momentum quickly.",
  "Five focused minutes still count as a win today.",
  "Try pairing the new habit with something you already do.",
  "Consistency improves when the routine feels simple and visible.",
  "A calm evening usually starts with fewer decisions after dinner.",
  "That sounds like progress worth protecting with a small daily cue.",
  "You do not need perfection, just a version you can repeat tomorrow.",
  "A good recovery plan matters more than a perfect streak.",
  "Tracking one or two core habits is usually enough to stay grounded.",
  "A brief walk can be a strong reset for both mood and focus.",
  "When energy is low, reduce the goal instead of skipping it completely.",
  "Use the environment to help you instead of relying on motivation alone.",
  "A stable bedtime routine often improves everything else around it.",
  "Small wins build identity faster than occasional big efforts.",
  "Notice what worked today and make it easier to repeat tomorrow.",
  "The best routine is the one you can follow on busy days too.",
  "Momentum grows when you remove friction from the starting step.",
  "It helps to define the minimum version of success for hard days.",
  "You are building proof that your habits can survive imperfect weeks.",
];

const USER_DETAILS = [
  "Logged after breakfast.",
  "Checking in before lunch.",
  "Reflecting after a work block.",
  "Adding notes from my evening routine.",
  "Tracking this after a short walk.",
  "Writing this down before bed.",
  "Quick update between meetings.",
  "Sharing a thought from my journal.",
];

const AI_DETAILS = [
  "Keep the next step visible.",
  "Protect the smallest repeatable action.",
  "Aim for calm consistency over intensity.",
  "Use a reminder that appears at the right moment.",
  "Review what made today easier.",
  "Let the environment support the habit.",
  "Reduce friction before increasing effort.",
  "Make tomorrow's version easier to start.",
];

function formatTime(msgIndex, total) {
  const daysAgo = Math.floor((total - msgIndex) / 200);
  if (daysAgo > 30) return `${Math.floor(daysAgo / 30)}mo ago`;
  if (daysAgo > 0) return `${daysAgo}d ago`;
  const hoursAgo = Math.floor((total - msgIndex) / 10);
  if (hoursAgo > 0) return `${hoursAgo}h ago`;
  return "Just now";
}

// Pre-generate all 10k messages once
function generateAllMessages(count) {
  const msgs = [];
  for (let i = 0; i < count; i++) {
    const isUser = i % 2 === 0;
    const sequence = i + 1;
    const topic = Math.floor(i / 2) + 1;
    const detail = isUser
      ? USER_DETAILS[Math.floor(i / 3) % USER_DETAILS.length]
      : AI_DETAILS[Math.floor(i / 3) % AI_DETAILS.length];
    const baseText = isUser
      ? USER_MESSAGES[i % USER_MESSAGES.length]
      : AI_REPLIES[i % AI_REPLIES.length];

    msgs.push({
      id: sequence,
      text: `#${sequence} · Thread ${topic}: ${baseText} ${detail}`,
      sender: isUser ? "user" : "ai",
      time: formatTime(i, count),
    });
  }
  return msgs;
}

const TOTAL_MESSAGES = 10000;
const BATCH_SIZE = 50;

function getBottomScrollTop(element) {
  if (!element) {
    return 0;
  }

  return Math.max(0, element.scrollHeight - element.clientHeight - 1);
}

export const ChatTab = () => {
  const allMessages = useMemo(() => generateAllMessages(TOTAL_MESSAGES), []);
  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);
  const [extraMessages, setExtraMessages] = useState([]);
  const [isLoadingOld, setIsLoadingOld] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const scrollRef = useRef(null);
  const nextIdRef = useRef(TOTAL_MESSAGES + 1);
  const mountedRef = useRef(false);
  const userScrolledRef = useRef(false);
  const loadingOldRef = useRef(false);

  const scrollToBottom = useCallback((force = false) => {
    if (scrollRef.current && (force || !userScrolledRef.current)) {
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = getBottomScrollTop(scrollRef.current);
        }
      });
    }
  }, []);

  const displayedMessages = useMemo(() => {
    const startIdx = Math.max(0, TOTAL_MESSAGES - visibleCount);
    return [...allMessages.slice(startIdx), ...extraMessages];
  }, [allMessages, visibleCount, extraMessages]);

  const hasMore = visibleCount < TOTAL_MESSAGES;

  useEffect(() => {
    const syncInitialScroll = () => {
      if (!scrollRef.current) {
        return;
      }

      scrollRef.current.scrollTop = getBottomScrollTop(scrollRef.current);
    };

    const firstFrame = requestAnimationFrame(() => {
      syncInitialScroll();
      requestAnimationFrame(() => {
        syncInitialScroll();
        mountedRef.current = true;
      });
    });

    return () => cancelAnimationFrame(firstFrame);
  }, []);

  useEffect(() => {
    if (!mountedRef.current) {
      return;
    }
    scrollToBottom();
  }, [extraMessages, isTyping, scrollToBottom]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
    userScrolledRef.current = !isNearBottom;

    // Infinite scroll at top — load older batch
    if (el.scrollTop < 60 && hasMore && !loadingOldRef.current) {
      loadingOldRef.current = true;
      setIsLoadingOld(true);
      const prevHeight = el.scrollHeight;

      setTimeout(() => {
        setVisibleCount(prev => Math.min(prev + BATCH_SIZE, TOTAL_MESSAGES));

        requestAnimationFrame(() => {
          if (el) {
            el.scrollTop = Math.max(0, el.scrollHeight - prevHeight);
          }
        });
        loadingOldRef.current = false;
        setIsLoadingOld(false);
      }, 400);
    }
  }, [hasMore]);

  const handleSend = useCallback((text) => {
    const userId = nextIdRef.current++;
    const userMsg = {
      id: userId,
      text: `#${userId} · Live message: ${text}`,
      sender: "user",
      time: "Just now",
    };
    setExtraMessages(prev => [...prev, userMsg]);
    userScrolledRef.current = false;

    setIsTyping(true);
    const delay = 500 + Math.random() * 500;
    setTimeout(() => {
      const responseText = Math.random() > 0.3
        ? AI_RESPONSES[Math.floor(Math.random() * AI_RESPONSES.length)]
        : `You said: "${text}"`;
      const aiId = nextIdRef.current++;

      const aiMsg = {
        id: aiId,
        text: `#${aiId} · Live reply: ${responseText}`,
        sender: "ai",
        time: "Just now",
      };
      setExtraMessages(prev => [...prev, aiMsg]);
      setIsTyping(false);
    }, delay);
  }, []);

  const loadedInfo = `${displayedMessages.length.toLocaleString()} of ${(TOTAL_MESSAGES + extraMessages.length).toLocaleString()}`;

  return (
    <div
      data-testid="chat-tab"
      className="flex flex-col relative z-10 h-full min-h-0"
      style={{
        background: "linear-gradient(180deg, rgba(16,12,8,0.72) 0%, rgba(8,8,8,0.42) 42%, rgba(42,31,18,0.26) 100%)",
      }}
    >
      <PageBreathingAura variant="chat" />

      {/* Header */}
      <div
        className="relative z-10 flex items-center gap-3 px-4 py-3"
        style={{
          borderBottom: '1px solid rgba(255,255,255,0.04)',
          animation: 'fadeIn 0.3s ease-out',
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
          background: 'linear-gradient(180deg, rgba(12,10,9,0.42) 0%, rgba(12,10,9,0.1) 100%)',
        }}
      >
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(245,158,11,0.1)' }}
        >
          <span className="text-sm" style={{ color: '#D8A84E' }}>AI</span>
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-[#F3E5D2]">Emily 2.0</p>
          <p className="text-[10px] uppercase tracking-[0.26em] text-[#8E785C]">Recalibration Room</p>
        </div>
        <span data-testid="chat-message-count" className="text-[10px] text-[#75654F]">{loadedInfo}</span>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        data-testid="chat-messages"
        className="relative z-10 flex-1 min-h-0 overflow-y-auto px-4 py-3"
        style={{
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          overflowAnchor: 'none',
          paddingBottom: '18px',
          touchAction: 'auto',
        }}
      >
        {isLoadingOld && (
          <div data-testid="loading-old-messages" className="flex justify-center py-3">
            <div className="flex gap-1">
              {[0, 1, 2].map(i => (
                <div
                  key={i}
                  className="w-1.5 h-1.5 rounded-full"
                  style={{
                    background: '#F59E0B',
                    animation: `pulseGlow 1s ease-in-out ${i * 0.2}s infinite`,
                  }}
                />
              ))}
            </div>
          </div>
        )}
        {!hasMore && (
          <p className="text-center text-[11px] text-zinc-600 py-2">Beginning of conversation (10,000 messages)</p>
        )}
        <div className="px-1 pt-2 pb-4" style={{ animation: 'fadeInUp 0.5s ease-out' }}>
          <p className="max-w-[14rem] text-[2rem] font-light leading-[1.2] text-[#F3E5D2]">
            Welcome to the Recalibration Room.
          </p>
          <p className="mt-4 max-w-[15rem] text-base leading-8 text-[#B7A792]">
            Before we begin, how does your breath feel in this moment?
          </p>
        </div>

        {displayedMessages.map((msg, idx) => (
          <div
            key={msg.id}
            style={{
              animation: `fadeInUp 0.45s ease-out ${Math.min(idx, 8) * 0.03}s both`,
            }}
          >
            <Message message={msg} />
          </div>
        ))}
        {isTyping && (
          <div data-testid="typing-indicator" className="flex justify-start mb-2">
            <div
              className="px-4 py-3 rounded-2xl rounded-bl-md flex gap-1.5"
              style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}
            >
              {[0, 1, 2].map(i => (
                <div
                  key={i}
                  className="w-1.5 h-1.5 rounded-full"
                  style={{
                    background: '#71717A',
                    animation: `pulseGlow 1s ease-in-out ${i * 0.15}s infinite`,
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <InputBox onSend={handleSend} />
    </div>
  );
};

export default ChatTab;
